import React from 'react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div
              className="flex items-center space-x-2 cursor-pointer"
              onClick={() => navigate('/')}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_ec527428-3aec-42a4-a1f7-d2e66c0be0df/artifacts/hktez0bk_logo_transs.png"
                alt="doorush logo"
                className="h-10 w-auto"
              />
            </div>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="border-2 border-[#007e7a] text-[#007e7a] hover:bg-[#007e7a] hover:text-white"
            >
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto prose prose-lg text-gray-700 leading-relaxed">

          <h1 className="text-4xl font-bold text-gray-900">
            Privacy Policy
          </h1>

          <p className="text-gray-600">
            Last Updated: 25/06/2025
          </p>

          <p>
            This Privacy Policy ("Policy") outlines how Holbewoner Technologies
            Private Limited ("doorush", "we", "us", or "our") collects, uses,
            stores, shares, and safeguards your personal and non-personal data
            when you use our mobile application, website, or any services
            (collectively referred to as the "Platform"). This Policy is issued
            in accordance with the Information Technology Act, 2000, the
            Information Technology (Reasonable Security Practices and Procedures
            and Sensitive Personal Data or Information) Rules, 2011, and is
            aligned with emerging expectations under India’s Digital Personal
            Data Protection Act, 2023 (DPDP Act).
          </p>

          <p>
            By using the doorush Platform, you agree to the practices described
            in this Policy. If you do not agree, you should not use the Platform
            or submit any information.
          </p>

          <h2>1. Information We Collect</h2>

          <p>We may collect the following categories of information:</p>

          <p>
            <strong>1.1 Personally Identifiable Information (PII):</strong>{" "}
            Includes your name, contact details, residential address, gender,
            date of birth, identity verification documents (if applicable),
            household information (e.g., number of rooms or presence of pets),
            and any additional data provided during account setup or service
            usage.
          </p>

          <p>
            <strong>1.2 Financial Information:</strong>{" "}
            Includes your UPI ID, card/bank information, payment gateway
            preferences, transaction timestamps, and billing data. Sensitive
            data is collected via secure and compliant channels only (e.g.,
            PCI-DSS compliant systems).
          </p>

          <p>
            <strong>1.3 Service Interaction Data:</strong>{" "}
            Data related to usage behavior on the platform — type of services
            used, time of booking, ratings, feedback, order history, and support
            requests.
          </p>

          <p>
            <strong>1.4 Device & Technical Information:</strong>{" "}
            We may collect technical information about the device used to access
            the Platform — such as IP address, operating system, browser type,
            language settings, device model, mobile carrier, advertising
            identifiers, and geolocation data (subject to permissions).
          </p>

          <p>
            <strong>1.5 Cookies and Tracking:</strong>{" "}
            We use cookies, pixels, SDKs, and web beacons to store user
            preferences, enable features, deliver ads, and improve user
            experience. Users can configure cookie settings in their browser.
          </p>

          <p>
            <strong>1.6 Communication Logs:</strong>{" "}
            All inbound/outbound communication including in-app chat, SMS,
            email, call records (where applicable), and WhatsApp conversations
            may be logged for quality assurance and dispute resolution.
          </p>

          <h2>2. Purpose of Collection and Use of Information</h2>

          <p>We collect and process your data for the following lawful purposes:</p>

          <ul>
            <li>To create and manage your doorush account</li>
            <li>To verify your identity and maintain account integrity</li>
            <li>To process and fulfill service bookings</li>
            <li>To facilitate real-time partner assignment and route tracking</li>
            <li>To send order confirmations, OTPs, alerts, invoices, and feedback forms</li>
            <li>To allow cancellation, refund processing, and payment reconciliation</li>
            <li>To analyze user behavior and improve service quality</li>
            <li>To customize content, recommend services, and personalize experience</li>
            <li>To prevent fraud, enforce platform integrity, and monitor suspicious behavior</li>
            <li>To enable platform diagnostics and customer support</li>
            <li>To ensure compliance with legal, tax, or regulatory obligations</li>
          </ul>

          <h2>3. Legal Basis for Processing (Under Indian Law)</h2>

          <p>We rely on one or more of the following legal grounds under Indian law:</p>

          <ul>
            <li><strong>Consent:</strong> When you provide your data voluntarily (e.g., creating an account or contacting support)</li>
            <li><strong>Contractual necessity:</strong> To perform obligations linked to your service bookings</li>
            <li><strong>Legitimate interest:</strong> For enhancing our services, fraud detection, or network security</li>
            <li><strong>Legal obligations:</strong> To comply with applicable laws, government orders, or judicial proceedings</li>
          </ul>

          <p>
            You have the right to withdraw consent at any time by emailing
            support@doorush.in, although certain functions may become
            unavailable.
          </p>

          <h2>4. Sharing and Disclosure of Information</h2>

          <p>We do not sell your personal information. We share it only under specific, lawful circumstances:</p>

          <ul>
            <li>With service providers (Quick Help / Insta Cook partners) to fulfill bookings</li>
            <li>With payment aggregators to complete financial transactions</li>
            <li>With vendors handling cloud hosting, analytics, SMS/email delivery, and support</li>
            <li>With marketing partners for advertising, remarketing, and campaign analysis (where consented)</li>
            <li>With government agencies, courts, regulators, or law enforcement where mandated</li>
            <li>During business transfers such as mergers, acquisitions, or restructuring</li>
          </ul>

          <p>
            All vendors and partners are bound by confidentiality agreements and
            operate in compliance with Indian data privacy regulations.
          </p>

          <h2>5. International Data Transfers</h2>

          <p>
            doorush aims to host and process data primarily within India.
            However, if any cross-border transfer of personal data is necessary
            (e.g., cloud storage or analytics support), it shall be:
          </p>

          <ul>
            <li>In accordance with applicable Indian laws</li>
            <li>Limited to jurisdictions offering equivalent data protection frameworks</li>
            <li>Protected through standard contractual clauses and best-practice safeguards</li>
          </ul>

          <h2>6. Data Retention and Deletion</h2>

          <p>
            We retain personal information only for as long as necessary for the
            purposes stated above, or as required by Indian tax, accounting, and
            legal standards. Retention periods include:
          </p>

          <ul>
            <li>For active users: until account is deleted or user withdraws consent</li>
            <li>For inactive users: 3 years from last interaction</li>
            <li>For financial or compliance records: up to 7 years</li>
            <li>For disputes or legal holds: until resolution or regulatory clearance</li>
          </ul>

          <p>
            Users may request deletion of personal data unless legal obligations
            require retention.
          </p>

          <h2>7. Your Rights Under Indian Law</h2>

          <ul>
            <li>Right to Access: Request a summary of data we hold about you</li>
            <li>Right to Correction: Request correction of outdated or inaccurate information</li>
            <li>Right to Deletion: Request deletion of data unless retained for legal reasons</li>
            <li>Right to Withdraw Consent: Opt-out of specific processing (e.g., marketing)</li>
            <li>Right to Lodge Complaints: File a complaint with the Grievance Officer or relevant authority</li>
          </ul>

          <p>
            To exercise any of the above, email: support@doorush.in with subject
            line “Data Rights Request.”
          </p>

          <h2>8. Data Security Measures</h2>

          <ul>
            <li>End-to-end encryption for sensitive fields (e.g., passwords, UPI IDs)</li>
            <li>SSL/TLS security for all communications</li>
            <li>Multi-factor authentication for internal access</li>
            <li>Regular penetration testing and vulnerability patching</li>
            <li>Role-based access control with audit logging</li>
          </ul>

          <p>
            In case of a data breach affecting your information, we will notify
            you and regulatory bodies as required by law.
          </p>

          <h2>9. Children’s Privacy</h2>

          <p>
            doorush is not intended for individuals under 18 years. We do not
            knowingly collect data from minors. If a minor’s data has been
            shared unintentionally, parents/guardians are requested to email us
            immediately.
          </p>

          <h2>10. Cookies, Analytics, and Third-Party Tracking</h2>

          <ul>
            <li>Manage sessions and login status</li>
            <li>Personalize ads (e.g., Facebook Pixel, Google Ads)</li>
            <li>Track traffic via analytics (e.g., Google Analytics, Firebase)</li>
          </ul>

          <p>
            Users can adjust cookie settings in their browsers and may opt out
            of personalized advertising via their device or browser settings.
          </p>

          <h2>11. Third-Party Links</h2>

          <p>
            The Platform may contain external links to apps, websites, or APIs.
            doorush is not responsible for third-party privacy practices. You
            are encouraged to review their policies before use.
          </p>

          <h2>12. Updates to Privacy Policy</h2>

          <p>
            We may amend this Policy periodically to reflect technological,
            regulatory, or business changes. Major updates will be notified via
            app pop-ups or email. Continued usage implies acceptance.
          </p>

          <h2>14. Consent and Binding Nature</h2>

          <p>
            By registering or using doorush, you:
          </p>

          <ul>
            <li>Consent to the collection and use of your data as per this Policy</li>
            <li>Agree to the binding nature of this Policy and its integration with our Terms of Use</li>
            <li>Understand your rights and obligations under applicable Indian privacy law</li>
          </ul>

          <h2>15. Electronic Record Disclaimer</h2>

          <p>
            This document is generated electronically under the Information
            Technology Act, 2000. It does not require physical or digital
            signatures.
          </p>

          <p>
            For queries regarding this Policy, write to support@doorush.in or
            visit our website at www.doorush.in/privacy.
          </p>

        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
